import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormGroup,
  Grid,
  Typography,
  DialogActions,
  IconButton,
  Box,
} from "@mui/material";
import { Form, Formik } from "formik";
import InputDropdown from "../../../../components/InputFields/InputDropdown";
import InputText from "../../../../components/InputFields/InputText";
import AxiosInstance from "../../../AxiosInstance";
import * as Yup from "yup";
import DollerInput from "../../../../components/InputFields/Doller";
import BlueButton from "../../../../components/Button/BlueButton";
import showToast from "../../../../components/Toast/Toster";
import WhiteButton from "../../../../components/Button/WhiteButton";

import { WhiteLoaderComponent } from "../../../../components/Icon/Index";
import { PictureAsPdf, Image, ZoomIn, ZoomOut, Close as CloseIcon, FileDownloadOutlined } from "@mui/icons-material";
import { useUserType } from "../../../../contexts/UserTypeContext";

const AddItems = ({
  modelOpen,
  setModelOpen,
  setSelectedProductAndService,
  selectedProductAndService,
  productsAndService,
  setShowUnitsSection,
  setShowHoursSection,
  setSelectedUnitsAndHours,
  showHoursSection,
  showUnitsSection,
  selectedProduct,
  selectedUnitsAndHours,
  UnitsAndHours,
  selectedAdminId,
  CompanyId,
  getData,
  showSquaresSection,
  setShowSquaresSection,
  inputValue,
  handleSelectChange,
  index,
  setNew = false,
  handleFileChange,
  uploadFile,
  file,
  error,
  closePreviewModal,
  previewFile,
  handleDragOver,
  handleDrop,
  handleFilePreview,
  handleRemoveFile,
  previewModalOpen,
  setShowFixedSection,
  showFixedSection,
  setLineItems,
  setInputValue,
  setSearchInput,
  handleCloseModal,
  setSelectedProduct,
}) => {
  const [loading, setLoading] = useState(false);
  const [formikRef, setFormikRef] = useState(null);
  const [imageZoom, setImageZoom] = useState(100);
  const [modalWidth, setModalWidth] = useState("sm");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [costTypeDropdownOpen, setCostTypeDropdownOpen] = useState(false);
  const imgRef = useRef(null);
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";
  useEffect(() => {
    if (selectedProduct && selectedProduct.Attachment) {
      handleFileChange(selectedProduct.Attachment, true);
    } else if (modelOpen && !selectedProduct) {
      handleRemoveFile();
    }
  }, [selectedProduct, modelOpen]);

  useEffect(() => {
    if (formikRef && file !== undefined) {
      formikRef.setFieldValue("Attachment", file || "");
    }
  }, [file, formikRef]);

  // Reset zoom when preview modal closes
  useEffect(() => {
    if (!previewModalOpen) {
      setImageZoom(100);
      setModalWidth("sm");
    }
  }, [previewModalOpen]);

  const updateImageSize = () => {
    if (imgRef.current && previewFile && previewModalOpen && !previewFile?.endsWith(".pdf")) {
      const img = imgRef.current;
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;
      
      if (naturalWidth > 0 && naturalHeight > 0) {
        const containerWidth = img.parentElement?.clientWidth || 560;
        const padding = 40;
        const availableWidth = containerWidth - padding;
        
        // Calculate image width based on zoom
        let imageWidth;
        if (imageZoom === 100) {
          imageWidth = Math.min(naturalWidth, availableWidth);
          img.style.width = "auto";
          img.style.maxWidth = "100%";
          img.style.height = "auto";
          img.style.maxHeight = "none";
        } else {
          const displayWidthAt100 = Math.min(naturalWidth, availableWidth);
          imageWidth = (displayWidthAt100 * imageZoom) / 100;
          img.style.width = `${imageWidth}px`;
          img.style.height = "auto";
          img.style.maxWidth = "none";
          img.style.maxHeight = "none";
        }
        
        // Adjust modal width based on image size and zoom
        const totalWidth = imageWidth + padding + 80; // image + padding + dialog padding
        if (totalWidth < 500) {
          setModalWidth("xs");
        } else if (totalWidth < 700) {
          setModalWidth("sm");
        } else if (totalWidth < 900) {
          setModalWidth("md");
        } else {
          setModalWidth("lg");
        }
      }
    }
  };

  useEffect(() => {
    updateImageSize();
  }, [imageZoom, previewFile, previewModalOpen]);

  const handleZoomIn = () => {
    setImageZoom((prev) => Math.min(prev + 25, 300)); // Max 300%
  };

  const handleZoomOut = () => {
    setImageZoom((prev) => Math.max(prev - 25, 50)); // Min 50%
  };

  const handleResetZoom = () => {
    setImageZoom(100);
  };

  const handleDownload = async () => {
    if (previewFile) {
      try {
        const response = await fetch(previewFile);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = previewFile.split("/").pop() || "attachment";
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);
      } catch (error) {
        console.error("Error downloading file:", error);
        // Fallback: try direct download
        const link = document.createElement("a");
        link.href = previewFile;
        link.download = previewFile.split("/").pop() || "attachment";
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
      }
    }
  };

  const getInitialValues = React.useMemo(() => {
    return {
      Type: selectedProduct ? selectedProduct.Type : "",
      Name: selectedProduct
        ? selectedProduct.Name
        : inputValue
        ? inputValue
        : "",
      Description: selectedProduct ? selectedProduct?.Description : "",
      Unit: selectedProduct ? selectedProduct.Unit : "",
      Fixed: selectedProduct ? selectedProduct.Fixed : "",
      CostPerFixed: selectedProduct ? selectedProduct?.CostPerFixed : 0,
      CostPerUnit: selectedProduct ? selectedProduct?.CostPerUnit : 0,
      Hourly: selectedProduct ? selectedProduct.Hourly : "",
      CostPerHour: selectedProduct ? selectedProduct?.CostPerHour : 0,
      Square: selectedProduct ? selectedProduct.Square : "",
      CostPerSquare: selectedProduct ? selectedProduct?.CostPerSquare : 0,
      Attachment:
        selectedProduct && selectedProduct?.Attachment
          ? selectedProduct.Attachment
          : "",
      IsNonTaxable: selectedProduct ? (selectedProduct?.IsNonTaxable || false) : false,
      UnitsAndHoursType: selectedProduct
        ? selectedProduct?.Unit && selectedProduct?.CostPerUnit
          ? "Unit"
          : selectedProduct?.Square && selectedProduct?.CostPerSquare
          ? "Sq. Ft."
          : selectedProduct?.Hourly && selectedProduct?.CostPerHour
          ? "Hourly"
          : selectedProduct?.Fixed && selectedProduct?.CostPerFixed
          ? "Fixed"
          : ""
        : "",
    };
  }, [selectedProduct, inputValue]);

  return (
    <Dialog
      fullWidth
      open={modelOpen}
      onClose={() => {
        setSubmitAttempted(false);
        handleCloseModal();
      }}
    >
      <DialogTitle
        className="text-blue-color"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "2px solid",
          borderColor: isDonationMode
            ? "var(--border-donor-color)"
            : "var(--border-orange-color)",
          pb: 1.5,
        }}
      >
        <Typography
          component="span"
          className="text-blue-color"
          sx={{ fontWeight: 700, fontSize: "1.125rem" }}
        >
          Enter Materials and Labor Form
        </Typography>
        <IconButton
          aria-label="close"
          size="small"
          onClick={() => {
            setSubmitAttempted(false);
            handleCloseModal();
          }}
          sx={{
            color: "var(--text-blue-color)",
            "& .MuiSvgIcon-root": { color: "inherit" },
            "&:hover": {
              backgroundColor: "rgba(15, 43, 70, 0.08)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2.5, pb: 2 }}>
        <Formik
          enableReinitialize={true}
          initialValues={getInitialValues}
          validateOnChange={true}
          validateOnBlur={true}
          validationSchema={Yup.object().shape({
            Type: Yup.string().required("Type is required"),
            Name: Yup.string().required("Name is required"),
            Description: Yup.string(),
            UnitsAndHoursType: Yup.string().required("Cost Type is required"),
            CostPerUnit:
              showUnitsSection &&
              Yup.number().required("Cost per Unit  required"),
            CostPerFixed:
              showFixedSection &&
              Yup.number().required("Cost per Fixed  required"),
            CostPerHour:
              showHoursSection &&
              Yup.number().required("Cost per Hourly required"),
            CostPerSquare:
              showSquaresSection &&
              Yup.number().required("Cost per square foot required"),
          })}
          // onSubmit={async (values, { resetForm, setFieldValue }) => {
          //   if (!values?.UnitsAndHoursType) {
          //     return;
          //   }

          //   setLoading(true);

          //   try {
          //     values.Attachment = file || values.Attachment;

          //     if (
          //       values?.Attachment &&
          //       typeof values?.Attachment !== "string"
          //     ) {
          //       try {
          //         const uploadedFilePath = await uploadFile(values?.Attachment);
          //         values["Attachment"] = uploadedFilePath;
          //       } catch (error) {
          //         console.error("Error uploading file:", error);
          //         showToast.error("Error uploading file.");
          //         setLoading(false);
          //         return;
          //       }
          //     } else if (typeof values?.Attachment === "string") {
          //       values["Attachment"] = values.Attachment;
          //     }

          //     values["companyId"] = CompanyId || " ";

          //     const object = {
          //       Type: values?.Type,
          //       Name: values?.Name,
          //       Description: values?.Description,
          //       Attachment: values?.Attachment || null,
          //       AddedAt: new Date(),
          //       companyId: CompanyId,
          //       ...(values?.UnitsAndHoursType === "Unit"
          //         ? {
          //             CostPerUnit: parseInt(values?.CostPerUnit),
          //             Unit: parseInt(values?.Unit) || 1,
          //             CostPerFixed: null,
          //             Fixed: null,
          //             CostPerSquare: null,
          //             Square: null,
          //             CostPerHour: null,
          //             Hourly: null,
          //           }
          //         : values?.UnitsAndHoursType === "Sq. Ft."
          //         ? {
          //             CostPerSquare: parseInt(values?.CostPerSquare),
          //             Square: parseInt(values?.Square) || 1,
          //             CostPerHour: null,
          //             Hourly: null,
          //             CostPerUnit: null,
          //             Unit: null,
          //             CostPerFixed: null,
          //             Fixed: null,
          //           }
          //         : values?.UnitsAndHoursType === "Fixed"
          //         ? {
          //             CostPerSquare: null,
          //             Square: null,
          //             CostPerHour: null,
          //             Hourly: null,
          //             CostPerUnit: null,
          //             Unit: null,
          //             CostPerFixed: parseInt(values?.CostPerFixed),
          //             Fixed: parseInt(values?.Fixed) || 1,
          //           }
          //         : {
          //             CostPerHour: parseInt(values?.CostPerHour),
          //             Hourly: parseInt(values?.Hourly) || 1,
          //             CostPerUnit: null,
          //             Unit: null,
          //             CostPerSquare: null,
          //             Square: null,
          //             CostPerFixed: null,
          //             Fixed: null,
          //           }),
          //     };

          //     let res;
          //     if (!selectedProduct) {
          //       res = await AxiosInstance.post(`/v1/material`, object);
          //       if (res?.data?.statusCode === 200) {
          //         if (setNew) {
          //           handleSelectChange(index, res?.data?.data);
          //         }
          //       }
          //     } else {
          //       res = await AxiosInstance.put(
          //         `/v1/material/${selectedAdminId}`,
          //         object
          //       );
          //     }

          //     if (res?.data?.statusCode === 200) {
          //       setModelOpen(false);
          //       getData();
          //       setSelectedUnitsAndHours("");
          //       setTimeout(() => {
          //         showToast.success(res?.data?.message);
          //       }, 500);
          //       resetForm(values);
          //       handleRemoveFile();
          //       setInputValue("");
          //       setSearchInput("");
          //     } else if (res?.data?.statusCode === 400) {
          //       getData();
          //       setSelectedUnitsAndHours("");
          //       setTimeout(() => {
          //         showToast.warning(res?.data?.message);
          //       }, 500);
          //     } else {
          //       setTimeout(() => {
          //         showToast.warning(
          //           res?.data?.message || "An unexpected error occurred"
          //         );
          //       }, 500);
          //     }
          //   } catch (error) {
          //     console.error("Submission error:", error);

          //     if (error?.response?.data?.statusCode === 400) {
          //       showToast.warning(error?.response?.data?.message);
          //     } else {
          //       showToast.error(
          //         error?.response?.data?.message ||
          //           error?.message ||
          //           "An error occurred"
          //       );
          //     }
          //   } finally {
          //     setLoading(false);
          //   }
          // }}
          onSubmit={async (values, { resetForm, setSubmitting, setFieldTouched, validateForm, setErrors }) => {
            setSubmitAttempted(true);
            
            // Touch all required fields
            setFieldTouched("Type", true);
            setFieldTouched("Name", true);
            setFieldTouched("UnitsAndHoursType", true);
            
            if (showUnitsSection) {
              setFieldTouched("CostPerUnit", true);
            }
            if (showFixedSection) {
              setFieldTouched("CostPerFixed", true);
            }
            if (showHoursSection) {
              setFieldTouched("CostPerHour", true);
            }
            if (showSquaresSection) {
              setFieldTouched("CostPerSquare", true);
            }

            // Wait for validation to complete
            await new Promise(resolve => setTimeout(resolve, 0));
            const validationErrors = await validateForm();

            // If there are validation errors, stop submission
            if (Object.keys(validationErrors).length > 0) {
              setErrors(validationErrors);
              setSubmitting(false);
              return;
            }

            // 🔹 If it's an update, check if any changes were made
            if (selectedProduct) {
              const hasChanges = (() => {
                const keysToCheck = [
                  "Type",
                  "Name",
                  "Description",
                  "CostPerUnit",
                  "CostPerHour",
                  "CostPerSquare",
                  "CostPerFixed",
                  "Attachment",
                  "IsNonTaxable",
                ];
                return keysToCheck.some((key) => {
                  if (key === "IsNonTaxable") {
                    const oldVal = selectedProduct[key] ?? false;
                    const newVal = values[key] ?? false;
                    return Boolean(oldVal) !== Boolean(newVal);
                  }
                  if (key === "Attachment") {
                    // For Attachment, check both file prop and values.Attachment
                    // file can be: undefined (not touched), null/"" (removed), or string (new file)
                    const oldVal = selectedProduct[key] ?? "";
                    // If file is explicitly set (even if null/empty), use it; otherwise use values.Attachment
                    const newVal = file !== undefined ? (file || "") : (values[key] || "");
                    return String(oldVal).trim() !== String(newVal).trim();
                  }
                  const oldVal = selectedProduct[key] ?? "";
                  const newVal = values[key] ?? "";
                  return String(oldVal).trim() !== String(newVal).trim();
                });
              })();

              if (!hasChanges) {
                showToast.warning("No changes detected to update.");
                return; // ⛔ Stop execution — don't call the API
              }
            }

            setLoading(true);

            try {
              values.Attachment = file || values.Attachment;

              if (
                values?.Attachment &&
                typeof values?.Attachment !== "string"
              ) {
                try {
                  const uploadedFilePath = await uploadFile(values?.Attachment);
                  values["Attachment"] = uploadedFilePath;
                } catch (error) {
                  const msg = (error?.response?.data?.message || "").toString();
                  const isFileTooLarge =
                    error?.response?.status === 413 ||
                    (error?.response?.status === 400 && /file size too large/i.test(msg));
                  if (isFileTooLarge) {
                    // Toast already shown by AxiosInstance interceptor
                  } else {
                    console.error("Error uploading file:", error);
                    showToast.error("Error uploading file.");
                  }
                  setLoading(false);
                  return;
                }
              } else if (typeof values?.Attachment === "string") {
                values["Attachment"] = values.Attachment;
              }

              values["companyId"] = CompanyId || " ";

              const object = {
                Type: values?.Type,
                Name: values?.Name,
                Description: values?.Description,
                Attachment: values?.Attachment || null,
                IsNonTaxable: values?.IsNonTaxable || false,
                AddedAt: new Date(),
                companyId: CompanyId,
                ...(values?.UnitsAndHoursType === "Unit"
                  ? {
                      CostPerUnit: parseFloat(values?.CostPerUnit),
                      Unit: parseInt(values?.Unit) || 1,
                      CostPerFixed: null,
                      Fixed: null,
                      CostPerSquare: null,
                      Square: null,
                      CostPerHour: null,
                      Hourly: null,
                    }
                  : values?.UnitsAndHoursType === "Sq. Ft."
                  ? {
                      CostPerSquare: parseFloat(values?.CostPerSquare),
                      Square: parseInt(values?.Square) || 1,
                      CostPerHour: null,
                      Hourly: null,
                      CostPerUnit: null,
                      Unit: null,
                      CostPerFixed: null,
                      Fixed: null,
                    }
                  : values?.UnitsAndHoursType === "Fixed"
                  ? {
                      CostPerSquare: null,
                      Square: null,
                      CostPerHour: null,
                      Hourly: null,
                      CostPerUnit: null,
                      Unit: null,
                      CostPerFixed: parseFloat(values?.CostPerFixed),
                      Fixed: parseInt(values?.Fixed) || 1,
                    }
                  : {
                      CostPerHour: parseFloat(values?.CostPerHour),
                      Hourly: parseInt(values?.Hourly) || 1,
                      CostPerUnit: null,
                      Unit: null,
                      CostPerSquare: null,
                      Square: null,
                      CostPerFixed: null,
                      Fixed: null,
                    }),
              };

              let res;
              if (!selectedProduct) {
                res = await AxiosInstance.post(`/v1/material`, object);
                if (res?.data?.statusCode === 200) {
                  if (setNew) {
                    handleSelectChange(index, res?.data?.data);
                  }
                }
              } else {
                res = await AxiosInstance.put(
                  `/v1/material/${selectedAdminId}`,
                  object
                );
              }

              if (res?.data?.statusCode === 200) {
                setSubmitAttempted(false);
                setModelOpen(false);
                getData(true); // Force refresh to show updated data
                setSelectedUnitsAndHours("");
                setTimeout(() => {
                  showToast.success(res?.data?.message);
                }, 500);
                resetForm(values);
                handleRemoveFile();
                setInputValue("");
                setSearchInput("");
              } else if (res?.data?.statusCode === 400) {
                getData(true); // Force refresh even on error
                setSelectedUnitsAndHours("");
                setTimeout(() => {
                  showToast.warning(res?.data?.message);
                }, 500);
              } else {
                setTimeout(() => {
                  showToast.warning(
                    res?.data?.message || "An unexpected error occurred"
                  );
                }, 500);
              }
            } catch (error) {
              console.error("Submission error:", error);

              if (error?.response?.data?.statusCode === 400) {
                showToast.warning(error?.response?.data?.message);
              } else {
                showToast.error(
                  error?.response?.data?.message ||
                    error?.message ||
                    "An error occurred"
                );
              }
            } finally {
              setLoading(false);
            }
          }}
        >
          {(formikProps) => {
            const {
              values,
              errors,
              touched,
              handleBlur,
              handleChange,
              setFieldValue,
              setFieldError,
              setFieldTouched,
            } = formikProps;

            // Check for actual validation errors (not just empty fields)
            const hasErrors = Boolean(
              errors?.Type ||
              errors?.Name ||
              errors?.UnitsAndHoursType ||
              (showUnitsSection && errors?.CostPerUnit) ||
              (showFixedSection && errors?.CostPerFixed) ||
              (showHoursSection && errors?.CostPerHour) ||
              (showSquaresSection && errors?.CostPerSquare)
            );
            
            // Check if any field with an error has been touched or submit was attempted
            // Also verify that the value is actually invalid before showing error
            const hasTouchedErrors = Boolean(
              (errors?.Type && (submitAttempted || touched?.Type) && (!selectedProductAndService || !values?.Type || String(values?.Type).trim() === "")) ||
              (errors?.Name && (submitAttempted || touched?.Name) && (!values?.Name || String(values?.Name).trim() === "")) ||
              (errors?.UnitsAndHoursType && (submitAttempted || touched?.UnitsAndHoursType)) ||
              (showUnitsSection && errors?.CostPerUnit && (submitAttempted || touched?.CostPerUnit) && (!values?.CostPerUnit || String(values?.CostPerUnit).trim() === "" || parseFloat(values?.CostPerUnit) <= 0)) ||
              (showFixedSection && errors?.CostPerFixed && (submitAttempted || touched?.CostPerFixed) && (!values?.CostPerFixed || String(values?.CostPerFixed).trim() === "" || parseFloat(values?.CostPerFixed) <= 0)) ||
              (showHoursSection && errors?.CostPerHour && (submitAttempted || touched?.CostPerHour) && (!values?.CostPerHour || String(values?.CostPerHour).trim() === "" || parseFloat(values?.CostPerHour) <= 0)) ||
              (showSquaresSection && errors?.CostPerSquare && (submitAttempted || touched?.CostPerSquare) && (!values?.CostPerSquare || String(values?.CostPerSquare).trim() === "" || parseFloat(values?.CostPerSquare) <= 0))
            );
            
            // Only show error message when there are actual validation errors that have been touched or submit attempted
            const shouldShowError = hasTouchedErrors;

            if (!formikRef) {
              setFormikRef(formikProps);
            }

            // Check if there are any changes when in update mode
            const hasChanges = (() => {
              if (!selectedProduct) return true; // Always enabled for new items
              
              const keysToCheck = [
                "Type",
                "Name",
                "Description",
                "CostPerUnit",
                "CostPerHour",
                "CostPerSquare",
                "CostPerFixed",
                "Attachment",
                "IsNonTaxable",
                "UnitsAndHoursType",
              ];
              
              return keysToCheck.some((key) => {
                if (key === "IsNonTaxable") {
                  const oldVal = selectedProduct[key] ?? false;
                  const newVal = values[key] ?? false;
                  return Boolean(oldVal) !== Boolean(newVal);
                }
                if (key === "Attachment") {
                  const oldVal = selectedProduct[key] ?? "";
                  const newVal = file !== undefined ? (file || "") : (values[key] || "");
                  return String(oldVal).trim() !== String(newVal).trim();
                }
                if (key === "UnitsAndHoursType") {
                  // Check the actual cost type based on which fields are populated
                  const oldType = selectedProduct?.Unit && selectedProduct?.CostPerUnit
                    ? "Unit"
                    : selectedProduct?.Square && selectedProduct?.CostPerSquare
                    ? "Sq. Ft."
                    : selectedProduct?.Hourly && selectedProduct?.CostPerHour
                    ? "Hourly"
                    : selectedProduct?.Fixed && selectedProduct?.CostPerFixed
                    ? "Fixed"
                    : "";
                  const newType = values[key] || "";
                  return oldType !== newType;
                }
                // For numeric fields, compare as numbers to handle string/number differences
                if (key === "CostPerUnit" || key === "CostPerHour" || key === "CostPerSquare" || key === "CostPerFixed") {
                  const oldVal = selectedProduct[key];
                  const newVal = values[key];
                  const oldNum = oldVal !== null && oldVal !== undefined ? Number(oldVal) : null;
                  const newNum = newVal !== null && newVal !== undefined && newVal !== "" ? Number(newVal) : null;
                  return oldNum !== newNum;
                }
                const oldVal = selectedProduct[key] ?? "";
                const newVal = values[key] ?? "";
                return String(oldVal).trim() !== String(newVal).trim();
              });
            })();

            return (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  formikProps.handleSubmit();
                }}
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    e.target.tagName !== "TEXTAREA" &&
                    !e.target.classList.contains("MuiAutocomplete-input")
                  ) {
                    e.preventDefault();
                  }
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2.5,
                    width: "100%",
                  }}
                >
                <Grid className="form-wrap" sx={{ m: 0, p: 0 }}>
                  <FormControl fullWidth>
                    <InputDropdown
                      options={productsAndService}
                      value={selectedProductAndService || null}
                      onOpen={() => {
                        setTypeDropdownOpen(true);
                      }}
                      onClose={() => {
                        setTypeDropdownOpen(false);
                        // Set touched when dropdown closes and field is empty
                        if (!selectedProductAndService || !selectedProductAndService?.Type) {
                          formikProps.setFieldTouched("Type", true);
                        }
                      }}
                      onChange={async (_, newValue) => {
                        setSelectedProductAndService(newValue);
                        setSelectedUnitsAndHours(null);
                        setShowFixedSection(null);
                        setShowSquaresSection(null);
                        setShowHoursSection(null);
                        setShowUnitsSection(null);
                        const fieldValue = newValue ? newValue?.Type : "";
                        setFieldTouched("Type", true);
                        
                        await setFieldValue("Type", fieldValue, false);
                        
                        // If a valid value is selected, immediately clear any existing error
                        if (newValue && fieldValue && fieldValue.trim() !== "") {
                          setFieldError("Type", undefined);
                        }
                      }}
                      label="Type"
                      required
                      placeholder="Select type"
                      inputValue={values?.Type}
                      onTextFieldChange={handleChange}
                      name="Type"
                      onBlur={(e) => {
                        handleBlur(e);
                        // Only set touched if dropdown is not open
                        if (!typeDropdownOpen) {
                          formikProps.setFieldTouched("Type", true);
                        }
                      }}
                      type="text"
                      getOptionLabel={(option) => option?.Type || ""}
                      filterOptions={(options, state) => {
                        return options?.filter((option) =>
                          option?.Type?.toLowerCase().includes(
                            state?.inputValue?.toLowerCase()
                          )
                        );
                      }}
                      error={Boolean(
                        errors?.Type && 
                        (submitAttempted || touched?.Type) &&
                        (!selectedProductAndService || !values?.Type || String(values?.Type).trim() === "")
                      )}
                      helperText={undefined}
                      showErrorOnly={true}
                      fieldHeight="44px"
                    />
                  </FormControl>
                </Grid>
                <Grid sx={{ m: 0, p: 0 }}>
                  <InputText
                    value={values?.Name}
                    onChange={(e) => {
                      handleChange(e);
                      if (touched?.Name && e.target.value && errors?.Name) {
                        formikProps.setFieldError("Name", undefined);
                      }
                    }}
                    onBlur={(e) => {
                      handleBlur(e);
                      formikProps.setFieldTouched("Name", true);
                    }}
                    name="Name"
                    label="Name"
                    required
                    placeholder="Enter Name"
                    type="text"
                    className="text-blue-color w-100 m-0 productFormName"
                    fieldHeight="44px"
                    error={Boolean(errors?.Name && (submitAttempted || touched?.Name))}
                    helperText={undefined}
                    showErrorOnly={true}
                  />
                </Grid>
                <Grid sx={{ m: 0, p: 0 }}>
                  <FormControl fullWidth required>
                    <InputDropdown
                      inputValue={values?.UnitsAndHoursType}
                      onTextFieldChange={handleChange}
                      options={UnitsAndHours}
                      value={selectedUnitsAndHours || null}
                      onOpen={() => {
                        setCostTypeDropdownOpen(true);
                      }}
                      onClose={() => {
                        setCostTypeDropdownOpen(false);
                        // Set touched when dropdown closes and field is empty
                        if (!selectedUnitsAndHours || !selectedUnitsAndHours?.Type) {
                          formikProps.setFieldTouched("UnitsAndHoursType", true);
                        }
                      }}
                      onChange={async (_, newValue) => {
                        setSelectedUnitsAndHours(newValue);
                        setShowUnitsSection(newValue?.Type === "Unit");
                        setShowHoursSection(newValue?.Type === "Hourly");
                        setShowSquaresSection(newValue?.Type === "Sq. Ft.");
                        setShowFixedSection(newValue?.Type === "Fixed");
                        const fieldValue = newValue ? newValue.Type : "";
                        setFieldTouched("UnitsAndHoursType", true);
                        
                        await setFieldValue("UnitsAndHoursType", fieldValue, false);
                        
                        // If a valid value is selected, immediately clear any existing error
                        if (newValue && fieldValue && fieldValue.trim() !== "") {
                          setFieldError("UnitsAndHoursType", undefined);
                        }
                      }}
                      label="Cost Type"
                      required
                      placeholder="Select cost type"
                      name="UnitsAndHoursType"
                      getOptionLabel={(option) => option.Type || ""}
                      filterOptions={(options, state) => {
                        return options?.filter((option) =>
                          option?.Type?.toLowerCase().includes(
                            state.inputValue?.toLowerCase()
                          )
                        );
                      }}
                      onBlur={(e) => {
                        handleBlur(e);
                        // Only set touched if dropdown is not open
                        if (!costTypeDropdownOpen) {
                          formikProps.setFieldTouched("UnitsAndHoursType", true);
                        }
                      }}
                      error={Boolean(
                        errors?.UnitsAndHoursType && (submitAttempted || touched?.UnitsAndHoursType)
                      )}
                      helperText={undefined}
                      showErrorOnly={true}
                      fieldHeight="44px"
                    />
                  </FormControl>
                </Grid>
                {showUnitsSection && (
                  <Grid className="d-flex w-100 gap-2" sx={{ m: 0, p: 0 }}>
                    <DollerInput
                      value={values?.CostPerUnit}
                      onBlur={(e) => {
                        handleBlur(e);
                        formikProps.setFieldTouched("CostPerUnit", true);
                      }}
                      onChange={(e) => {
                        const value = e?.target?.value;
                        if (/^\d*\.?\d*$/.test(value)) {
                          handleChange({
                            target: {
                              name: "CostPerUnit",
                              value,
                            },
                          });
                          if (touched?.CostPerUnit && value && errors?.CostPerUnit) {
                            formikProps.setFieldError("CostPerUnit", undefined);
                          }
                        }
                      }}
                      name="CostPerUnit"
                      placeholder="Enter Unit"
                      label="Cost"
                      required
                      type="number"
                      fieldHeight="44px"
                      className="w-100 accountInformationInputTop"
                      error={Boolean(
                        errors?.CostPerUnit && 
                        (submitAttempted || touched?.CostPerUnit) &&
                        (!values?.CostPerUnit || String(values?.CostPerUnit).trim() === "" || parseFloat(values?.CostPerUnit) <= 0)
                      )}
                      helperText={undefined}
                      showErrorOnly={true}
                    />
                  </Grid>
                )}

                {showHoursSection && (
                  <Grid className="d-flex gap-2 w-100">
                    <DollerInput
                      value={values?.CostPerHour}
                      onBlur={(e) => {
                        handleBlur(e);
                        formikProps.setFieldTouched("CostPerHour", true);
                      }}
                      onChange={(e) => {
                        const value = e?.target?.value;
                        if (/^\d*\.?\d*$/.test(value)) {
                          handleChange({
                            target: {
                              name: "CostPerHour",
                              value,
                            },
                          });
                          if (touched?.CostPerHour && value && errors?.CostPerHour) {
                            formikProps.setFieldError("CostPerHour", undefined);
                          }
                        }
                      }}
                      name="CostPerHour"
                      placeholder="Enter Hour"
                      label="Cost"
                      required
                      type="number"
                      fieldHeight="44px"
                      className="w-100 accountInformationInputTop"
                      error={Boolean(
                        errors?.CostPerHour && 
                        (submitAttempted || touched?.CostPerHour) &&
                        (!values?.CostPerHour || String(values?.CostPerHour).trim() === "" || parseFloat(values?.CostPerHour) <= 0)
                      )}
                      helperText={undefined}
                      showErrorOnly={true}
                    />
                  </Grid>
                )}
                {showSquaresSection && (
                  <Grid className="d-flex gap-2 w-100" sx={{ m: 0, p: 0 }}>
                    <DollerInput
                      value={values?.CostPerSquare}
                      onBlur={(e) => {
                        handleBlur(e);
                        formikProps.setFieldTouched("CostPerSquare", true);
                      }}
                      onChange={(e) => {
                        const value = e?.target?.value;
                        if (/^\d*\.?\d*$/.test(value)) {
                          handleChange({
                            target: {
                              name: "CostPerSquare",
                              value,
                            },
                          });
                          if (touched?.CostPerSquare && value && errors?.CostPerSquare) {
                            formikProps.setFieldError("CostPerSquare", undefined);
                          }
                        }
                      }}
                      name="CostPerSquare"
                      placeholder="Enter Square"
                      label="Cost"
                      required
                      type="number"
                      fieldHeight="44px"
                      className="w-100 accountInformationInputTop"
                      error={Boolean(
                        errors?.CostPerSquare && 
                        (submitAttempted || touched?.CostPerSquare) &&
                        (!values?.CostPerSquare || String(values?.CostPerSquare).trim() === "" || parseFloat(values?.CostPerSquare) <= 0)
                      )}
                      helperText={undefined}
                      showErrorOnly={true}
                    />
                  </Grid>
                )}
                {showFixedSection && (
                  <Grid className="d-flex gap-2 w-100" sx={{ m: 0, p: 0 }}>
                    <DollerInput
                      value={values?.CostPerFixed}
                      onBlur={(e) => {
                        handleBlur(e);
                        formikProps.setFieldTouched("CostPerFixed", true);
                      }}
                      onChange={(e) => {
                        const value = e?.target?.value;
                        if (/^\d*\.?\d*$/.test(value)) {
                          handleChange({
                            target: {
                              name: "CostPerFixed",
                              value,
                            },
                          });
                          if (touched?.CostPerFixed && value && errors?.CostPerFixed) {
                            formikProps.setFieldError("CostPerFixed", undefined);
                          }
                        }
                      }}
                      name="CostPerFixed"
                      placeholder="Enter Fixed"
                      label="Cost"
                      required
                      type="number"
                      fieldHeight="44px"
                      className="w-100 accountInformationInputTop"
                      error={Boolean(
                        errors?.CostPerFixed && 
                        (submitAttempted || touched?.CostPerFixed) &&
                        (!values?.CostPerFixed || String(values?.CostPerFixed).trim() === "" || parseFloat(values?.CostPerFixed) <= 0)
                      )}
                      helperText={undefined}
                      showErrorOnly={true}
                    />
                  </Grid>
                )}

                <Grid sx={{ m: 0, p: 0 }}>
                  <FormGroup>
                    <InputText
                      id="exampleText"
                      placeholder="Enter description"
                      name="Description"
                      label="Description"
                      type="textarea"
                      value={values?.Description}
                      onBlur={(e) => {
                        handleBlur(e);
                        formikProps.setFieldTouched("Description", true);
                      }}
                      onChange={(e) => {
                        handleChange(e);
                        if (touched?.Description && e.target.value && errors?.Description) {
                          formikProps.setFieldError("Description", undefined);
                        }
                      }}
                      className="text-blue-color w-100 m-0"
                      style={{ marginTop: 0 }}
                      fieldHeight="120px"
                      minRows={3}
                      error={false}
                      helperText={undefined}
                      showErrorOnly={true}
                    />
                  </FormGroup>
                </Grid>

                <Grid sx={{ m: 0, p: 0, pt: 0.5 }}>
                  <FormGroup>
                    <Grid className="d-flex align-items-center">
                      <input
                        type="checkbox"
                        id="IsNonTaxable"
                        name="IsNonTaxable"
                        checked={values?.IsNonTaxable || false}
                        onChange={(e) => {
                          setFieldValue("IsNonTaxable", e.target.checked);
                        }}
                        style={{
                          width: "18px",
                          height: "18px",
                          marginRight: "8px",
                          cursor: "pointer",
                        }}
                      />
                      <label
                        htmlFor="IsNonTaxable"
                        style={{
                          cursor: "pointer",
                          fontSize: "15px",
                          fontWeight: 500,
                          color: "#0f2b46",
                          margin: 0,
                        }}
                        className="text-blue-color"
                      >
                        Non-Taxable
                      </label>
                    </Grid>
                  </FormGroup>
                </Grid>

                <Grid className="product-items-attach" sx={{ m: 0, p: 0 }}>
                  <Grid
                    className="file-upload"
                    onDragOver={handleDragOver}
                    onDrop={(e) => {
                      handleDrop(e);
                    }}
                    style={{
                      borderRadius: "10px",
                      padding: "20px",
                      textAlign: "center",
                      backgroundColor: "#f9f9f9",
                      marginTop: 0,
                    }}
                  >
                    <input
                      type="file"
                      id="file-input"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        handleFileChange(e);
                      }}
                      accept=".jpg,.jpeg,.png"
                    />
                    <label
                      htmlFor="file-input"
                      className="text-blue-color"
                      style={{
                        cursor: "pointer",
                        display: "block",
                        borderRadius: "10px",
                        textAlign: "center",
                      }}
                    >
                      <span className="mb-2">Add Receipt</span>
                      <br />
                      <span
                        style={{
                          marginTop: "8px",
                          display: "block",
                          border: "none",
                        }}
                      >
                        or drag an image here to upload
                      </span>
                    </label>
                  </Grid>
                  {file && (
                    <Grid
                      className="materials-labor-file-preview"
                      style={{
                        marginTop: "15px",
                        padding: "10px",
                        border: "1px solid #ccc",
                        borderRadius: "10px",
                        backgroundColor: "#fff",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        overflow: "hidden",
                        overflowX: "auto",
                        scrollbarWidth: "thin",
                      }}
                    >
                      <Typography style={{ margin: 0 }}>
                        {file?.endsWith(".pdf") ? (
                          <PictureAsPdf
                            style={{
                              fontSize: "24px",
                              marginRight: "8px",
                              color: "var(--amber)",
                            }}
                          />
                        ) : (
                          <Image
                            style={{
                              fontSize: "24px",
                              marginRight: "8px",
                              color: "var(--amber)",
                            }}
                          />
                        )}
                        <strong className="text-orange-color">
                          Uploaded File :
                        </strong>{" "}
                        <a
                          onClick={() => handleFilePreview(file)}
                          style={{
                            textDecoration: "none",
                            color: "var(--amber)",
                            cursor: "pointer",
                          }}
                        >
                          {file?.split("/")?.pop() || ""}
                        </a>
                      </Typography>
                      <button
                        onClick={handleRemoveFile}
                        style={{
                          border: "none",
                          background: "none",
                          cursor: "pointer",
                          fontSize: "20px",
                          color: "red",
                          marginLeft: "10px",
                        }}
                      >
                        &times;
                      </button>
                    </Grid>
                  )}
                  <Dialog
                    open={previewModalOpen}
                    onClose={closePreviewModal}
                    maxWidth={modalWidth}
                    fullWidth={false}
                    PaperProps={{
                      style: {
                        maxHeight: "90vh",
                        height: "auto",
                        display: "flex",
                        flexDirection: "column",
                        width: "auto",
                        maxWidth: modalWidth === "xs" ? "400px" : modalWidth === "sm" ? "500px" : modalWidth === "md" ? "700px" : "900px",
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        boxShadow: "var(--shadow-xl)",
                      },
                    }}
                  >
                    <DialogTitle
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 16px",
                        borderBottom: "1px solid #e0e0e0",
                        backgroundColor: "#fff",
                      }}
                    >
                      <Typography 
                        className="text-blue-borerBommoModel"
                        style={{ fontWeight: 600, fontSize: "18px" }}
                      >
                        File Preview
                      </Typography>
                      {!previewFile?.endsWith(".pdf") && (
                        <Box 
                          style={{ 
                            display: "flex", 
                            gap: "4px", 
                            alignItems: "center",
                            backgroundColor: "#f5f5f5",
                            padding: "4px 8px",
                            borderRadius: "8px",
                          }}
                        >
                          <IconButton
                            onClick={handleZoomOut}
                            disabled={imageZoom <= 50}
                            size="small"
                            style={{ 
                              color: imageZoom <= 50 ? "#ccc" : "#0f2b46",
                              padding: "4px",
                            }}
                            title="Zoom Out"
                          >
                            <ZoomOut fontSize="small" />
                          </IconButton>
                          <Typography
                            variant="body2"
                            style={{
                              minWidth: "50px",
                              textAlign: "center",
                              color: "#0f2b46",
                              fontWeight: 600,
                              fontSize: "14px",
                              padding: "0 8px",
                            }}
                          >
                            {imageZoom}%
                          </Typography>
                          <IconButton
                            onClick={handleZoomIn}
                            disabled={imageZoom >= 300}
                            size="small"
                            style={{ 
                              color: imageZoom >= 300 ? "#ccc" : "#0f2b46",
                              padding: "4px",
                            }}
                            title="Zoom In"
                          >
                            <ZoomIn fontSize="small" />
                          </IconButton>
                          <Button
                            onClick={handleResetZoom}
                            size="small"
                            variant="outlined"
                            style={{ 
                              color: "#0f2b46",
                              borderColor: "#0f2b46",
                              marginLeft: "8px",
                              padding: "2px 12px",
                              fontSize: "12px",
                              textTransform: "none",
                              minWidth: "auto",
                            }}
                            title="Reset Zoom"
                          >
                            Reset
                          </Button>
                          <IconButton
                            onClick={handleDownload}
                            size="small"
                            style={{ 
                              color: "#0f2b46",
                              marginLeft: "8px",
                              padding: "4px",
                            }}
                            title="Download"
                          >
                            <FileDownloadOutlined fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={closePreviewModal}
                            size="small"
                            style={{ 
                              color: "#0f2b46", 
                              marginLeft: "8px",
                              padding: "4px",
                            }}
                            title="Close"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                      {previewFile?.endsWith(".pdf") && (
                        <Box style={{ display: "flex", gap: 1, alignItems: "center" }}>
                          <IconButton
                            onClick={handleDownload}
                            size="small"
                            style={{ 
                              color: "#0f2b46",
                            }}
                            title="Download"
                          >
                            <FileDownloadOutlined fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={closePreviewModal}
                            size="small"
                            style={{ color: "#0f2b46" }}
                            title="Close"
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      )}
                    </DialogTitle>
                    <DialogContent
                      style={{
                        textAlign: "center",
                        padding: "0",
                        overflow: "auto",
                        backgroundColor: "#f5f5f5",
                        position: "relative",
                        flex: "1 1 auto",
                        minHeight: 0,
                        maxHeight: "calc(90vh - 120px)",
                      }}
                    >
                      {previewFile?.endsWith(".pdf") ? (
                        <iframe
                          src={previewFile}
                          style={{
                            width: "100%",
                            height: "50vh",
                            border: "none",
                            borderRadius: "8px",
                          }}
                          title="File Preview"
                        />
                      ) : (
                        <Box
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: "40vh",
                            padding: "20px",
                            width: "100%",
                            boxSizing: "border-box",
                            overflow: "auto",
                          }}
                        >
                          <img
                            ref={imgRef}
                            src={previewFile}
                            alt="Preview"
                            onLoad={updateImageSize}
                            style={{
                              width: "auto",
                              maxWidth: imageZoom === 100 ? "100%" : "none",
                              height: "auto",
                              borderRadius: "8px",
                              boxShadow: "var(--shadow-md)",
                              objectFit: "contain",
                              transition: "width 0.3s ease",
                              display: "block",
                            }}
                            draggable={false}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/assets/images/no-image.png";
                            }}
                          />
                        </Box>
                      )}
                    </DialogContent>
                    <DialogActions style={{ padding: "8px 24px" }}>
                      <Button
                        onClick={closePreviewModal}
                        className="bg-blue-color text-white"
                        variant="contained"
                      >
                        Close
                      </Button>
                    </DialogActions>
                  </Dialog>

                  {error && (
                    <Grid
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "4px",
                        marginLeft: "0px",
                        paddingLeft: "0px",
                      }}
                    >
                      {error}
                    </Grid>
                  )}
                </Grid>
                {shouldShowError && (
                  <Grid className="mt-2 mb-2">
                    <Typography
                      sx={{
                        color: "#dc3545",
                        fontSize: "14px",
                        fontWeight: 500,
                        textAlign: "center",
                      }}
                    >
                      Please correct the red boxes above
                    </Typography>
                  </Grid>
                )}
                </Box>
                <Grid
                  className="d-flex gap-2 justify-content-between align-items-center productBtnNavigate"
                  sx={{ mt: 2, pt: 1 }}
                >
                  <WhiteButton
                    className="text-blue-color border-blue-color materials-labor-modal-cancel"
                    onClick={() => {
                      setSubmitAttempted(false);
                      if (handleRemoveFile) handleRemoveFile();
                      if (setModelOpen) setModelOpen(false);
                      if (setSelectedProduct) setSelectedProduct(null);
                      if (setSelectedUnitsAndHours)
                        setSelectedUnitsAndHours("");
                      if (setSelectedProductAndService)
                        setSelectedProductAndService(null);
                      if (setShowUnitsSection) setShowUnitsSection(false);
                      if (setShowHoursSection) setShowHoursSection(false);
                      if (setShowSquaresSection) setShowSquaresSection(false);
                      if (setShowFixedSection) setShowFixedSection(false);
                    }}
                    label="Cancel"
                  />
                  <BlueButton
                    label={
                      loading ? (
                        <WhiteLoaderComponent
                          height="20"
                          width="20"
                          padding="20"
                          loader={loading}
                        />
                      ) : selectedProduct ? (
                        "Edit"
                      ) : (
                        "Add"
                      )
                    }
                    className="addProdyuctBtn"
                    disabled={loading || (selectedProduct && !hasChanges)}
                    type="submit"
                  />
                </Grid>
              </form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
export default AddItems;
